/**
 * Seeded, serializable pseudo-random number generator.
 *
 * - Algorithm: xoshiro128** (32-bit state words), seeded from a string with cyrb128.
 * - `snapshot()` / `Rng.restore()` make the generator part of the (JSON) game state.
 * - `fork(label)` derives an independent stream from the *root seed and label only*, not
 *   from how much has been consumed, so adding a draw in one module never shifts the
 *   sequences of another (GDD / PLAN §5.2).
 */

export type RngState = readonly [number, number, number, number];

export interface RngSnapshot {
  readonly seed: string;
  readonly state: RngState;
}

/** 128-bit string hash by bryc (public domain). Returns four uint32 words. */
function cyrb128(input: string): [number, number, number, number] {
  let h1 = 1779033703;
  let h2 = 3144134277;
  let h3 = 1013904242;
  let h4 = 2773480762;
  for (let i = 0; i < input.length; i++) {
    const k = input.charCodeAt(i);
    h1 = h2 ^ Math.imul(h1 ^ k, 597399067);
    h2 = h3 ^ Math.imul(h2 ^ k, 2869860233);
    h3 = h4 ^ Math.imul(h3 ^ k, 951274213);
    h4 = h1 ^ Math.imul(h4 ^ k, 2716044179);
  }
  h1 = Math.imul(h3 ^ (h1 >>> 18), 597399067);
  h2 = Math.imul(h4 ^ (h2 >>> 22), 2869860233);
  h3 = Math.imul(h1 ^ (h3 >>> 17), 951274213);
  h4 = Math.imul(h2 ^ (h4 >>> 19), 2716044179);
  h1 ^= h2 ^ h3 ^ h4;
  h2 ^= h1;
  h3 ^= h1;
  h4 ^= h1;
  return [h1 >>> 0, h2 >>> 0, h3 >>> 0, h4 >>> 0];
}

const rotl = (x: number, k: number): number => ((x << k) | (x >>> (32 - k))) >>> 0;

const TWO_POW_32 = 0x1_0000_0000;

export class Rng {
  readonly seed: string;
  private s0: number;
  private s1: number;
  private s2: number;
  private s3: number;

  private constructor(seed: string, state: RngState) {
    this.seed = seed;
    const [a, b, c, d] = state;
    // The all-zero state is a fixed point of xoshiro; never allow it.
    const zero = (a | b | c | d) === 0;
    this.s0 = zero ? 1 : a >>> 0;
    this.s1 = b >>> 0;
    this.s2 = c >>> 0;
    this.s3 = d >>> 0;
  }

  /** Creates a generator from a seed string. Same seed → same sequence, on every platform. */
  static create(seed: string): Rng {
    return new Rng(seed, cyrb128(seed));
  }

  /** Rebuilds a generator from `snapshot()`. */
  static restore(snapshot: RngSnapshot): Rng {
    return new Rng(snapshot.seed, snapshot.state);
  }

  /** Creates a generator from raw words. Exposed for tests against reference vectors. */
  static fromState(seed: string, state: RngState): Rng {
    return new Rng(seed, state);
  }

  snapshot(): RngSnapshot {
    return { seed: this.seed, state: [this.s0, this.s1, this.s2, this.s3] };
  }

  /** Independent stream named `label`, derived from the root seed only. */
  fork(label: string): Rng {
    return Rng.create(`${this.seed}/${label}`);
  }

  /** Next uint32 (xoshiro128**). */
  nextU32(): number {
    const result = Math.imul(rotl(Math.imul(this.s1, 5), 7), 9) >>> 0;
    const t = (this.s1 << 9) >>> 0;
    this.s2 = (this.s2 ^ this.s0) >>> 0;
    this.s3 = (this.s3 ^ this.s1) >>> 0;
    this.s1 = (this.s1 ^ this.s2) >>> 0;
    this.s0 = (this.s0 ^ this.s3) >>> 0;
    this.s2 = (this.s2 ^ t) >>> 0;
    this.s3 = rotl(this.s3, 11);
    return result;
  }

  /** Uniform integer in [min, max], both inclusive, without modulo bias. */
  int(min: number, max: number): number {
    if (!Number.isInteger(min) || !Number.isInteger(max) || max < min) {
      throw new RangeError(`Rng.int: invalid range [${min}, ${max}]`);
    }
    const range = max - min + 1;
    if (range > TWO_POW_32) throw new RangeError('Rng.int: range exceeds 2^32');
    const limit = Math.floor(TWO_POW_32 / range) * range;
    let x = this.nextU32();
    while (x >= limit) x = this.nextU32();
    return min + (x % range);
  }

  /** True with probability `permille / 1000` (integer arithmetic only). */
  chance(permille: number): boolean {
    return this.int(0, 999) < permille;
  }

  pick<T>(items: readonly T[]): T {
    if (items.length === 0) throw new RangeError('Rng.pick: empty array');
    return items[this.int(0, items.length - 1)] as T;
  }

  /** Returns a shuffled copy (Fisher–Yates). The input is not modified. */
  shuffle<T>(items: readonly T[]): T[] {
    const out = items.slice();
    for (let i = out.length - 1; i > 0; i--) {
      const j = this.int(0, i);
      const tmp = out[i] as T;
      out[i] = out[j] as T;
      out[j] = tmp;
    }
    return out;
  }

  /** Index chosen with probability proportional to the integer `weights`. */
  weightedIndex(weights: readonly number[]): number {
    let total = 0;
    for (const w of weights) {
      if (!Number.isInteger(w) || w < 0) throw new RangeError('Rng.weightedIndex: bad weight');
      total += w;
    }
    if (total === 0) throw new RangeError('Rng.weightedIndex: all weights are zero');
    let roll = this.int(0, total - 1);
    for (let i = 0; i < weights.length; i++) {
      roll -= weights[i] as number;
      if (roll < 0) return i;
    }
    throw new Error('unreachable');
  }
}
